/**
 * Preklad hernej udalosti do riadku priebehu.
 *
 * Riadok nie je len text: nesie **druh** (podľa neho ho log farebne odlíši),
 * **hráča** (farebná bodka) a rozpad na časti, kde časť s `cardId` je konkrétna
 * karta — log z nej spraví náhľad pod kurzorom. Vďaka tomu sa dá spätne
 * pozrieť, čo tá karta vlastne robila, bez hľadania v balíčku.
 */

import type { CardId, GameEvent } from '@unmatched/engine';
import { content } from '@unmatched/content';

/** Stačí nám to, čo majú spoločné plný aj redigovaný stav. */
interface NamedState {
  cards: Record<string, { defId: string }>;
  fighters: { id: string; name: string }[];
  /** Figúrky mimo dosky (Vanish) — v logu ich treba vedieť pomenovať. */
  limbo?: { id: string; name: string }[];
  /** Mapa zápasu — priestory v logu po mene, nie po ID. */
  mapId?: string;
}

function spaceName(state: NamedState, spaceId: string): string {
  const map = state.mapId ? content.maps[state.mapId] : undefined;
  return map?.spaces.find((s) => s.id === spaceId)?.label ?? spaceId;
}

export type LogKind =
  | 'info'
  | 'turn'
  | 'action'
  | 'move'
  | 'card'
  | 'combat'
  | 'damage'
  | 'heal'
  | 'defeat'
  | 'end';

export interface LogPart {
  text: string;
  /** Ak je vyplnené, časť sa v logu chová ako náhľad karty. */
  cardId?: CardId;
}

export interface LogEntry {
  kind: LogKind;
  /** Koho sa riadok týka; `null` = udalosť zápasu, nie hráča. */
  player: number | null;
  parts: LogPart[];
}

function cardPart(state: NamedState, cardId: CardId | null): LogPart {
  if (!cardId) return { text: 'kartu' };
  const instance = state.cards[cardId];
  const def = instance ? content.cards[instance.defId] : undefined;
  return def ? { text: `${def.name} (${def.value})`, cardId } : { text: cardId };
}

/** Zoznam kariet oddelený čiarkami, kde každá zostane samostatným náhľadom. */
function withCommas(state: NamedState, cardIds: CardId[]): LogPart[] {
  return cardIds.flatMap((cardId, index) =>
    index === 0 ? [cardPart(state, cardId)] : [{ text: ', ' }, cardPart(state, cardId)],
  );
}

function fighterName(state: NamedState, fighterId: string): string {
  return (
    state.fighters.find((f) => f.id === fighterId)?.name ??
    state.limbo?.find((f) => f.id === fighterId)?.name ??
    fighterId
  );
}

export function eventEntry(state: NamedState, event: GameEvent): LogEntry {
  const t = (kind: LogKind, player: number | null, ...parts: (string | LogPart)[]): LogEntry => ({
    kind,
    player,
    parts: parts.map((part) => (typeof part === 'string' ? { text: part } : part)),
  });

  switch (event.type) {
    case 'gameStarted':
      return t('info', null, `Hra začala na mape ${content.maps[event.mapId]?.name ?? event.mapId}`);
    case 'turnStarted':
      return t('turn', event.player, `Ťah ${event.turnNumber}`);
    case 'actionUsed':
      return t('action', event.player, {
        maneuver: 'akcia: manéver',
        scheme: 'akcia: plán',
        attack: 'akcia: útok',
      }[event.kind]);
    case 'fighterMoved':
      return t(
        'move',
        null,
        `${fighterName(state, event.fighterId)}: ${event.path.map((id) => spaceName(state, id)).join(' → ')}`,
      );
    case 'cardDrawn':
      return event.cardId
        ? t('card', event.player, 'ťahá ', cardPart(state, event.cardId), ` · ruka ${event.handSize}`)
        : t('card', event.player, `ťahá kartu · ruka ${event.handSize}`);
    case 'maneuverStarted':
      return t('action', event.player, 'manévruje');
    case 'maneuverBoosted':
      return t('card', event.player, `boost +${event.amount} · `, cardPart(state, event.cardId));
    case 'maneuverEnded':
      return t('action', event.player, 'manéver ukončený');
    case 'exhausted':
      return t('damage', event.player, 'VYČERPANÝ — prázdny balíček, každá figúrka dostáva 2');
    case 'cardDiscarded':
      return t(
        'card',
        event.player,
        event.reason === 'handLimit'
          ? 'odhadzuje na limit ruky '
          : event.reason === 'boost'
            ? 'boost kartou '
            : event.reason === 'effect'
              ? 'odhadzuje (efekt) '
              : 'odhadzuje ',
        cardPart(state, event.cardId),
      );
    case 'combatStarted':
      return t(
        'combat',
        event.attackerPlayer,
        `${event.bonus ? `bonusový útok ${event.bonus}` : 'súboj'}: ${fighterName(state, event.attackerFighter)} → ${fighterName(state, event.defenderFighter)}`,
      );
    case 'combatCardsRevealed':
      return t(
        'combat',
        null,
        'odhalenie: ',
        cardPart(state, event.attackCard),
        ' vs ',
        event.defenseCard ? cardPart(state, event.defenseCard) : { text: 'bez obrany' },
      );
    case 'combatValues':
      return t('combat', null, `hodnoty: útok ${event.attack} · obrana ${event.defense}`);
    case 'effectCancelled':
      return t('combat', event.player, 'efekty karty zrušené — ', cardPart(state, event.cardId));
    case 'healed':
      return t(
        'heal',
        null,
        `${fighterName(state, event.fighterId)} sa lieči o ${event.amount} · má ${event.hpAfter}`,
      );
    case 'cardsReturned':
      return t('card', event.player, `berie si z odhodu ${event.cardIds.length} kariet späť`);
    case 'handRevealed':
      return event.cards
        ? t('card', event.player, 'pozerá súperovi do ruky: ', ...withCommas(state, event.cards))
        : t('card', event.player, 'pozerá súperovi do ruky');
    case 'specialUsed':
      return t('action', event.player, `schopnosť: ${event.name}`);
    case 'choiceSkipped':
      return t('info', event.player, `efekt nepoužitý — ${event.label}`);
    case 'damageDealt':
      return event.amount === 0
        ? t('info', null, `${fighterName(state, event.fighterId)} — poškodenie zastavené ochranou`)
        : t(
            'damage',
            null,
            `${fighterName(state, event.fighterId)} dostáva ${event.amount} · zostáva ${event.hpAfter}`,
          );
    case 'fighterDefeated':
      return t('defeat', null, `${fighterName(state, event.fighterId)} — mimo boja`);
    case 'actionGained':
      return t('action', event.player, `získava akciu navyše (+${event.amount})`);
    case 'fightersSwapped':
      return t(
        'move',
        null,
        `${fighterName(state, event.a)} a ${fighterName(state, event.b)} si vymenili miesta`,
      );
    case 'attackerChanged':
      return t('combat', null, `útočníkom je teraz ${fighterName(state, event.fighterId)}`);
    case 'attackCardReplaced':
      return t('combat', event.player, 'mení útočnú kartu: ', cardPart(state, event.from), ' → ', cardPart(state, event.to));
    case 'fighterRevived':
      return t('heal', null, `${fighterName(state, event.fighterId)} sa vracia do boja · ${event.hp} zdravia`);
    case 'defenderChanged':
      return t('combat', null, `obrancom je teraz ${fighterName(state, event.fighterId)}`);
    case 'tokenPlaced':
      return t('move', null, `hmla položená: ${spaceName(state, event.spaceId)}`);
    case 'tokenMoved':
      return t('move', null, `hmla: ${spaceName(state, event.from)} → ${spaceName(state, event.to)}`);
    case 'fighterRemoved':
      return t('move', null, `${fighterName(state, event.fighterId)} odchádza z dosky`);
    case 'fighterReturned':
      return t(
        'move',
        null,
        `${fighterName(state, event.fighterId)} sa objavuje: ${spaceName(state, event.spaceId)}`,
      );
    case 'cardToDeck':
      return event.cardId
        ? t('card', event.player, 'kladie navrch balíčka ', cardPart(state, event.cardId))
        : t('card', event.player, 'kladie kartu navrch balíčka');
    case 'combatEnded':
      return t('combat', null, 'súboj skončil');
    case 'turnEnded':
      return t('action', event.player, 'končí ťah');
    case 'gameEnded':
      return t('end', event.winner, 'KONIEC ZÁPASU');
    case 'ongoingPlayed':
      return t('card', event.player, 'trvalá schéma ostáva ležať: ', cardPart(state, event.cardId));
    case 'ongoingEnded':
      return t('card', event.player, 'trvalá schéma končí: ', cardPart(state, event.cardId));
    case 'cardSearched':
      return t('card', event.player, 'berie si z balíčka ', cardPart(state, event.cardId));
    case 'cardRevealed':
      return t('card', event.player, 'odhaľuje ', cardPart(state, event.cardId));
    case 'deckMilled':
      return t('card', event.player, 'z balíčka rovno do odhodu: ', ...withCommas(state, event.cardIds));
    case 'deckShuffled':
      return t('card', event.player, 'mieša si balíček');
    case 'deckReordered':
      return t('card', event.player, 'pozerá na vrch súperovho balíčka a mení poradie kariet');
    case 'discardToDeck':
      return t('card', event.player, event.bottom ? 'z odhodu pod balíček ' : 'z odhodu navrch balíčka ', cardPart(state, event.cardId));
    case 'handShuffledIn':
      return t('card', event.player, `zamiešava ${event.count} kariet z ruky do balíčka`);
    case 'cardsToBottom':
      return t('card', event.player, `dáva ${event.count} kariet z vrchu pod balíček`);
    default:
      return t('info', null, JSON.stringify(event));
  }
}
